import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Activity,
  LayoutGrid,
  FileText,
  Shield,
  Users,
  Settings,
  HardDrive,
  Plug,
  Wrench,
  Code,
  LogOut,
  ScanLine,
  Shapes,
  Crown,
} from 'lucide-react';
import { useAuthContext } from '../../../components/auth/AuthProvider';
import { useRBAC } from '../../../components/auth/RBACContext';
import OrgSwitcher from '../../../components/auth/OrgSwitcher';

/**
 * Menu item definition. The `pageKey` is the entry in PAGE_ACCESS used
 * to decide whether this item is visible to the current user. By default
 * we derive a key from the path (e.g. `/analytics` → `analytics`); set
 * `pageKey` for any path that doesn't map 1:1.
 */
interface MenuItem {
  path: string;
  icon: React.ReactNode;
  tooltip: string;
  pageKey?: string;
  /** True for items every authenticated user can see (e.g. Home). */
  alwaysVisible?: boolean;
}

export default function Sidebar() {
  const location = useLocation();
  const { isAuthenticated, logout } = useAuthContext();
  const { canAccess, isSuperAdmin, isPlatformSupport, organizations } = useRBAC();

  const menuItems: MenuItem[] = [
    { path: '/', icon: <Home size={20} />, tooltip: 'Home', alwaysVisible: true },
    { path: '/analytics', icon: <Activity size={20} />, tooltip: 'Analytics' },
    { path: '/tasks', icon: <FileText size={20} />, tooltip: 'Tasks' },
    { path: '/document-extraction', icon: <ScanLine size={20} />, tooltip: 'OCR Tracking', pageKey: 'ocr-tracking' },
    { path: '/processes', icon: <LayoutGrid size={20} />, tooltip: 'Processes' },
    { path: '/incidents', icon: <Shield size={20} />, tooltip: 'Incidents' },
    { path: '/iam', icon: <Users size={20} />, tooltip: 'IAM' },
    { path: '/storage', icon: <HardDrive size={20} />, tooltip: 'Storage' },
    { path: '/integrations', icon: <Plug size={20} />, tooltip: 'Integrations' },
    { path: '/studio', icon: <Code size={20} />, tooltip: 'Studio' },
    { path: '/tools', icon: <Wrench size={20} />, tooltip: 'Tools' },
  ];

  /**
   * Resolve which menu item should light up for the current pathname.
   * We pick the longest matching item path so a more specific shortcut
   * (e.g. `/studio/templateEngine`) wins over its parent (`/studio`) —
   * without this both icons end up highlighted whenever the user is on
   * a sub-page that two menu items happen to prefix.
   */
  const activePath = (() => {
    if (location.pathname === '/') return '/';
    let bestMatch = '';
    for (const item of menuItems) {
      if (item.path === '/') continue;
      if (
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + '/')
      ) {
        if (item.path.length > bestMatch.length) bestMatch = item.path;
      }
    }
    return bestMatch;
  })();

  const isActive = (path: string) => path === activePath;

  const handleLogout = () => {
    logout();
  };

  /**
   * Filter via PAGE_ACCESS as the single source of truth — no per-item
   * `roles` array. Super-admins and platform-support get the full list;
   * everyone else sees only what `canAccess` admits.
   */
  const visibleMenuItems = menuItems.filter((item) => {
    if (item.alwaysVisible) return true;
    if (isSuperAdmin || isPlatformSupport) return true;
    const pageName = item.pageKey ?? item.path.replace(/^\//, '');
    return canAccess(pageName);
  });

  return (
    <div className="fixed left-0 top-0 h-full w-16 bg-slate-50 border-r border-gray-200 flex flex-col items-center py-4 z-50">
      {/* Logo */}
      <div className="w-12 h-12 mb-4 flex items-center justify-center flex-shrink-0">
        <img
          src={`${import.meta.env.BASE_URL}pmdglogo.png`}
          alt="PMDG logo"
          className="h-10 w-10 object-contain"
        />
      </div>

      {/* Super Admin badge */}
      {isSuperAdmin && (
        <div className="mb-4 relative group flex-shrink-0">
          <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
            <Crown size={14} className="text-amber-600" />
          </div>
          <div className="absolute left-8 top-0 px-2 py-1 bg-amber-600 text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-50">
            Super Admin
          </div>
        </div>
      )}

      {/*
        Scrollable middle region. `min-h-0` is essential — without it a
        flex child won't shrink below its content height inside a column
        flex container, so `overflow-y-auto` would never engage and the
        bottom block (Settings / Org switcher / Logout / avatar) would
        be pushed off-screen when the nav grows past the viewport.

        Scrollbar is hidden (no track, no thumb) so the gutter doesn't
        slice the right edge and push the 40-px icons off-centre in the
        64-px column — wheel / trackpad scroll still works.
        `[&::-webkit-scrollbar]:hidden` applies Tailwind's
        `display: none` utility to the WebKit scrollbar pseudo-element;
        `[scrollbar-width:none]` is the Firefox equivalent.

        Tooltips inside SidebarButton would normally be clipped here —
        when `overflow-y` is non-visible, browsers force `overflow-x` to
        auto as well, so the tooltip's horizontal extension past the
        sidebar's right edge gets cut off. The button renders its
        tooltip via React Portal into document.body to escape that.
      */}
      <nav
        className="flex-1 min-h-0 flex flex-col gap-3 w-full px-2 overflow-y-auto
                   [scrollbar-width:none]
                   [&::-webkit-scrollbar]:hidden"
      >
        {visibleMenuItems.map((item) => (
          <SidebarButton
            key={item.path}
            icon={item.icon}
            to={item.path}
            active={isActive(item.path)}
            tooltip={item.tooltip}
          />
        ))}

        {/* Settings / Org switcher / Logout now live INSIDE the
            scrollable nav so they flow with the rest of the items.
            Only the user-profile avatar at the very bottom is pinned —
            that gives short viewports more breathing room while still
            keeping the avatar one click away. */}
        {(isSuperAdmin || isPlatformSupport || canAccess('settings')) && (
          <SidebarButton
            icon={<Settings size={20} />}
            to="/settings"
            active={isActive('/settings')}
            tooltip="Settings"
          />
        )}

        {(isSuperAdmin || isPlatformSupport || organizations.length > 1) && (
          <OrgSwitcher />
        )}

        {isAuthenticated && <SidebarLogoutButton onLogout={handleLogout} />}
      </nav>

      {/* Pinned user-profile avatar — the only element that stays
          anchored to the bottom of the column. */}
      <div className="flex flex-col w-full px-2 flex-shrink-0 pt-2">
        <div className="w-9 h-9 mx-auto rounded-full overflow-hidden border-2 border-white shadow-sm relative group cursor-pointer ring-2 ring-gray-100 hover:ring-blue-100 transition-all">
          <img
            src="https://images.pexels.com/photos/1037995/pexels-photo-1037995.jpeg?auto=compress&cs=tinysrgb&w=100"
            alt="User"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-white" />
        </div>
      </div>
    </div>
  );
}

function SidebarButton({
  icon,
  to,
  active = false,
  tooltip,
}: {
  icon: React.ReactNode;
  to: string;
  active?: boolean;
  tooltip: string;
}) {
  // The tooltip is rendered via a portal into document.body so the
  // scrollable `<nav>` parent can't clip it horizontally (browsers
  // force overflow-x to auto whenever overflow-y is set to anything
  // other than visible). We track hover state manually and compute the
  // fixed position from the link's bounding rect.
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    if (!hovered || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  }, [hovered]);

  return (
    <div
      className="relative flex justify-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link
        ref={buttonRef}
        to={to}
        className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200 ${active
            ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 translate-x-1'
            : 'text-gray-500 hover:text-gray-900 hover:bg-white hover:shadow-sm'
          }`}
      >
        {icon}
      </Link>
      {hovered &&
        createPortal(
          <div
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
            className="fixed -translate-y-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg whitespace-nowrap pointer-events-none z-[100] shadow-xl border border-gray-700/50"
          >
            {tooltip}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[4px] w-2 h-2 bg-gray-900 rotate-45 border-l border-b border-gray-700/50"></div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function SidebarLogoutButton({ onLogout }: { onLogout: () => void }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    if (!hovered || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  }, [hovered]);

  return (
    <div
      className="relative flex justify-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        ref={buttonRef}
        onClick={onLogout}
        className="w-12 h-12 flex items-center justify-center rounded-xl transition-all text-gray-500 hover:text-red-600 hover:bg-red-50 hover:shadow-sm"
      >
        <LogOut size={20} />
      </button>
      {hovered &&
        createPortal(
          <div
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
            className="fixed -translate-y-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg whitespace-nowrap pointer-events-none z-[100] shadow-xl border border-gray-700/50"
          >
            Sign Out
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[4px] w-2 h-2 bg-gray-900 rotate-45 border-l border-b border-gray-700/50"></div>
          </div>,
          document.body,
        )}
    </div>
  );
}
